import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getRecoveryTimeline } from "../../../service/api";

interface TimelineTask {
    _id: string;
    taskName: string;
    priority: string;
    status: string;
    startDate?: string;
    targetDate?: string;
    progress: number;
    assignedUserName?: string;
}

const RecoveryTimeline: React.FC = () => {
    const navigate = useNavigate();
    const { planId } = useParams<{ planId: string }>();
    const [tasks, setTasks] = useState<TimelineTask[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (planId) {
            fetchTimeline();
        }
    }, [planId]);

    const fetchTimeline = async () => {
        setLoading(true);
        try {
            const response = await getRecoveryTimeline(planId!);
            if (response.data?.resCode === 200 && response.data?.data) {
                const timelineTasks = Array.isArray(response.data.data)
                    ? response.data.data
                    : [];
                setTasks(timelineTasks);
            }
        } catch (error: any) {
            console.error("Error fetching recovery timeline:", error);
            toast.error(
                error.response?.data?.resMessage || "Failed to fetch recovery timeline"
            );
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status: string) => {
        const colors: Record<string, string> = {
            Pending: "bg-gray-400",
            "In Progress": "bg-blue-500",
            Completed: "bg-green-500",
            Blocked: "bg-red-500",
        };
        return colors[status] || "bg-gray-400";
    };

    const getPriorityColor = (priority: string) => {
        const colors: Record<string, string> = {
            Critical: "border-red-500",
            High: "border-orange-500",
            Medium: "border-yellow-500",
            Low: "border-green-500",
        };
        return colors[priority] || "border-gray-500";
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString();
    };

    const calculateDuration = (start?: string, end?: string) => {
        if (!start || !end) return 0;
        const startDate = new Date(start);
        const endDate = new Date(end);
        const diff = endDate.getTime() - startDate.getTime();
        return Math.ceil(diff / (1000 * 60 * 60 * 24)); // days
    };

    return (
        <div className="px-2 py-4 mt-14 w-full">
            <div className="bg-white rounded-lg shadow-lg p-6">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">
                            Recovery Timeline
                        </h1>
                        <p className="text-sm text-gray-600 mt-1">
                            Visual timeline of recovery tasks
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => navigate(`/external-emergency-preparedness/bcp-plans/${planId}/recovery-tasks`)}
                            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                        >
                            Back to Tasks
                        </button>
                    </div>
                </div>

                {/* Legend */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Status Legend:</h3>
                    <div className="flex gap-4 flex-wrap">
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-gray-400 rounded"></div>
                            <span className="text-sm">Pending</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-blue-500 rounded"></div>
                            <span className="text-sm">In Progress</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-green-500 rounded"></div>
                            <span className="text-sm">Completed</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-red-500 rounded"></div>
                            <span className="text-sm">Blocked</span>
                        </div>
                    </div>
                </div>

                {/* Timeline */}
                {loading ? (
                    <div className="text-center py-8">
                        <p className="text-gray-600">Loading timeline...</p>
                    </div>
                ) : tasks.length === 0 ? (
                    <div className="text-center py-8">
                        <p className="text-gray-600">No tasks found for this BCP plan</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {tasks.map((task, index) => {
                            const duration = calculateDuration(task.startDate, task.targetDate);
                            return (
                                <div
                                    key={task._id}
                                    className={`border-l-4 ${getPriorityColor(task.priority)} bg-white rounded-lg shadow p-4`}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <div className="flex-1">
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                {task.taskName}
                                            </h3>
                                            <p className="text-sm text-gray-600 mt-1">
                                                Assigned to: {task.assignedUserName || "Unassigned"}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${getStatusColor(
                                                    task.status
                                                )}`}
                                            >
                                                {task.status}
                                            </span>
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                                                {task.priority}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                                        <div>
                                            <p className="text-xs text-gray-500">Start Date</p>
                                            <p className="text-sm font-medium text-gray-900">
                                                {formatDate(task.startDate)}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Target Date</p>
                                            <p className="text-sm font-medium text-gray-900">
                                                {formatDate(task.targetDate)}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Duration</p>
                                            <p className="text-sm font-medium text-gray-900">
                                                {duration > 0 ? `${duration} days` : "N/A"}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="mt-3">
                                        <div className="flex justify-between items-center mb-1">
                                            <p className="text-xs text-gray-500">Progress</p>
                                            <p className="text-xs font-semibold text-gray-900">
                                                {task.progress}%
                                            </p>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className={`h-2 rounded-full ${getStatusColor(task.status)}`}
                                                style={{ width: `${task.progress}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RecoveryTimeline;
